import { createAuth } from 'vue-auth3';
import router from '../../../resources/js/router';
import axios from 'axios';
import driverAuthBearer from 'vue-auth3/dist/drivers/auth/bearer-token';

import { hasActiveContextPermission, requirementMet } from '@lib/utils/context';

import type { AuthUser } from '@lib/types';

const auth = createAuth({
	rolesKey: 'permissions',
	stores: ['storage'], //, "cookie"],
	staySignedInKey: 'auth_stay_signed_in',
	tokenDefaultKey: 'auth_token',
	//
	authRedirect: '/login',
	forbiddenRedirect: '/403',
	notFoundRedirect: '/404',
	//
	loginData: {
		url: 'auth/login',
		method: 'POST',
		redirect: { name: 'Home' },
		fetchUser: true,
		// Persist the token only when the user opts in via the "Stay signed in"
		// checkbox; the login page passes staySignedIn per request. When false the
		// token lives in sessionStorage and is cleared once the browser closes.
		staySignedIn: false,
	},
	fetchData: {
		enabled: true,
		cache: false,
		// On app boot the token refresh and the user fetch would otherwise run in
		// parallel with the same token; the refresh blacklists it server-side and
		// the user fetch then 401s. Wait for the refresh so the fetch uses the new token.
		waitRefresh: true,
	},
	refreshToken: {
		// POST: refreshing issues a token, the backend takes no GET for it
		method: 'POST',
		// Refresh the short-lived access token on app load and, via the interval,
		// every 50 minutes so a session kept open outlives the 60-minute JWT_TTL.
		// The backend JWT_REFRESH_TTL caps how long after the login a token can be renewed.
		enabled: true,
		enabledInBackground: true,
		interval: 50,
	},
	plugins: {
		router,
	},
	drivers: {
		auth: driverAuthBearer,
		http: {
			request: axios,
		},
	},
});

// Without a token the logout request cannot be authenticated and only earns a 401. That happens on every
// rejected token at boot: the 401 interceptor below drops the token, then vue-auth3's own handler logs out.
const logoutWithRequest = auth.logout.bind(auth);
auth.logout = ((data) =>
	logoutWithRequest({
		...data,
		makeRequest: (data?.makeRequest ?? auth.options.logoutData.makeRequest) && !!auth.token(),
	})) as typeof auth.logout;

// Add a context to the auth for easier access
auth.checkContext = function (permission: string, context: string): boolean {
	if (!this.check()) return false;

	// Check if user exists
	const user = this.user<AuthUser>();
	if (!user) return false;

	// Direct context permission check; 'Team', 'team' and 'teams' name the same kind
	if (hasActiveContextPermission(user, context, permission)) {
		return true;
	}

	// The same bypass the backend's CheckContextPermission middleware applies, so the two agree
	// about who may reach a context-guarded route. rolesKey is 'permissions', so check() asks
	// the permission array.
	if (this.check('context.bypass')) {
		return true;
	}

	// All checks failed reject access
	return false;
};

// Todo: understand why this is needed
axios.interceptors.request.use((config) => {
	if (auth.check()) {
		config.headers['Authorization'] = 'Bearer ' + auth.token();
	}
	return config;
});

/*
 * Token refresh outside the 50 minute interval
 *
 * The interval alone is not enough: a sleeping laptop or a throttled background tab skips it, and the
 * first request after waking carries an expired token. That 401 used to put up the lock screen of
 * the dashboard layout, although the session was still good for days.
 */

// Shared by every caller, so a burst of 401s or a wake-up costs one refresh, not one each
let refreshing: Promise<unknown> | null = null;
function refreshOnce(): Promise<unknown> {
	refreshing ??= auth.refresh().finally(() => {
		refreshing = null;
	});
	return refreshing;
}

/** Expiry of a JWT in ms since the epoch, null for a value that is not one. */
function tokenExpiresAt(token: string | null): number | null {
	try {
		const payload = JSON.parse(atob((token ?? '').split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
		return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
	} catch {
		return null;
	}
}

// Refresh when the tab comes back with a token that has run out, or is about to
function refreshIfStale() {
	if (document.visibilityState !== 'visible' || !auth.check()) return;

	const expiresAt = tokenExpiresAt(auth.token());
	if (expiresAt !== null && expiresAt - Date.now() < 5 * 60 * 1000) {
		// A failure is handled by the auth/refresh branch of the interceptor below
		refreshOnce().catch(() => undefined);
	}
}
document.addEventListener('visibilitychange', refreshIfStale);
globalThis.addEventListener('online', refreshIfStale);

const AUTH_URL = /(^|\/)auth\/(user|refresh)(\?|$)/;
const LOGIN_OR_LOGOUT_URL = /(^|\/)auth\/(login|logout)(\?|$)/;

/** The token a failed request was sent with. */
function sentToken(error: any): string {
	return String(error?.config?.headers?.Authorization ?? '').replace(/^Bearer\s+/, '');
}

// vue-auth3 keeps a stored token even when the server rejects it (it only self-clears a token that
// is already gone). So when the initial user fetch is answered with 401 - a stale token, e.g. after
// the account was removed - clear it locally and fall back to a clean login instead of looping on 401s.
//
// auth/refresh needs the same treatment: the backend answers 401 once a token is past
// JWT_REFRESH_TTL or has been blacklisted, and vue-auth3's own handler only logs out when the token
// is already missing from storage - which it never is here. Without this, a session that lost its
// token retries every 50 minutes forever; a wall-mounted dashboard did exactly that for hours.
axios.interceptors.response.use(
	(response) => response,
	async (error) => {
		const url: string = error?.config?.url ?? '';
		const status: number | undefined = error?.response?.status;

		// A locked user, on any request of a session: it is over. The 423 of a locked event has no
		// flag, and the login page reports a locked account itself.
		if (status === 423 && error?.response?.data?.user_locked && auth.token()) {
			auth.logout({ makeRequest: false, redirect: '/login' });
			return Promise.reject(error);
		}

		if (status === 401 && AUTH_URL.test(url)) {
			// Multi-tab guard. With "stay signed in" the token lives in localStorage, so every tab
			// shares it. If one tab refreshes first it blacklists the old token server-side, and a
			// second tab's in-flight refresh then comes back 401 even though the session is alive -
			// logging out there would kick the user out of a perfectly good session. Only log out
			// when the token that actually failed is still the one we hold; a different value means
			// another tab has already rotated it and the caller should simply retry.
			const failedToken = sentToken(error);
			if (!failedToken || failedToken === auth.token()) {
				auth.logout({ makeRequest: false, redirect: '/login' });
			}
			return Promise.reject(error);
		}

		// Any other request: its token expired, or a refresh rotated it while the request was in
		// flight. Refresh unless that already happened, then send the request once more - the
		// request interceptor puts the current token on it. Only a second 401 reaches the lock screen.
		const config = error?.config;
		if (status === 401 && config && !config._authRetried && auth.token() && !LOGIN_OR_LOGOUT_URL.test(url)) {
			config._authRetried = true;
			try {
				if (sentToken(error) === auth.token()) {
					await refreshOnce();
				}
			} catch {
				return Promise.reject(error);
			}
			return axios.request(config);
		}

		return Promise.reject(error);
	},
);

// Route permission guard - runs after auth is registered so auth.check() is available
router.beforeEach((to) => {
	const permissions: string[] | undefined = to.meta.permissions as string[] | undefined;
	if (!permissions || permissions.length === 0) return true;

	const hasAll = permissions.every((p) => auth.check(p));
	if (!hasAll) return { name: 'Forbidden' };

	return true;
});

// Route context guard: the selection a route asks for in meta.context and meta.mode
router.beforeEach((to) => {
	if (!to.meta.context && !to.meta.mode) return true;

	const user = auth.user<AuthUser>();
	if (!user || to.name === 'Home') return true;

	return requirementMet(user, { context: to.meta.context, mode: to.meta.mode }) || { name: 'Home' };
});

export default auth;
